import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Tour } from "../types";
import { TourCard } from "./TourCard";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface TourListProps {
  tours: Tour[];
  isLoading: boolean;
  error: string | null;
  onLoadMore: () => void;
}

// TODO: Refactor this code

export function TourList({
  tours,
  isLoading,
  error,
  onLoadMore,
}: TourListProps) {
  const { t } = useTranslation();

  const scheduledTours = tours.filter((tour) => tour.state === "ready");
  const inProgressTours = tours.filter((tour) => tour.state === "ongoing");
  const completedTours = tours.filter((tour) => tour.state === "completed");

  return (
    <Tabs defaultValue="scheduled" className="h-full flex flex-col">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="scheduled">
          {t("tours.states.scheduled")}
          <span className="ml-1">{scheduledTours.length}</span>
        </TabsTrigger>
        <TabsTrigger value="in-progress">
          {t("tours.states.inProgress")}
          <span className="ml-1">{inProgressTours.length}</span>
        </TabsTrigger>
        <TabsTrigger value="completed">
          {t("tours.states.completed")}
          <span className="ml-1">{completedTours.length}</span>
        </TabsTrigger>
      </TabsList>

      <TourListContent
        scheduledTours={scheduledTours}
        inProgressTours={inProgressTours}
        completedTours={completedTours}
        onLoadMore={onLoadMore}
        isLoading={isLoading}
        error={error}
      />
    </Tabs>
  );
}

interface TourListContentProps {
  scheduledTours: Tour[];
  inProgressTours: Tour[];
  completedTours: Tour[];
  isLoading: boolean;
  error: string | null;
  onLoadMore: () => void;
}

function TourListContent({
  scheduledTours,
  inProgressTours,
  completedTours,
  isLoading,
  error,
  onLoadMore,
}: TourListContentProps) {
  if (isLoading) {
    return (
      <div className="w-full pt-32 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorList error={error} onLoadMore={onLoadMore} />;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <TabContent
        value="scheduled"
        tours={scheduledTours}
        onLoadMore={onLoadMore}
      />
      <TabContent
        value="in-progress"
        tours={inProgressTours}
        onLoadMore={onLoadMore}
      />
      <TabContent
        value="completed"
        tours={completedTours}
        onLoadMore={onLoadMore}
      />
    </div>
  );
}

interface TabContentProps {
  value: string;
  tours: Tour[];
  onLoadMore: () => void;
}

function TabContent({ value, tours, onLoadMore }: TabContentProps) {
  return (
    <TabsContent value={value} className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tours.map((tour) => (
          <TourCard key={tour.id} tour={tour} />
        ))}
      </div>
      {tours.length === 0 && <EmptyList onLoadMore={onLoadMore} />}
    </TabsContent>
  );
}

function EmptyList({ onLoadMore }: { onLoadMore: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="my-24 text-center text-gray-500 flex flex-col justify-center items-center h-32">
      <div className="space-y-2 max-w-[28rem]">
        <h3 className="font-semibold text-xl">{t("tours.empty.title")}</h3>
        <p className="text-md font-medium opacity-60">
          {t("tours.empty.description")}
        </p>

        <Button
          variant="outline"
          className="h-8 rounded-full mt-4"
          onClick={() => onLoadMore()}
        >
          {t("tours.empty.button")}
        </Button>
      </div>
    </div>
  );
}

type ErrorListProps = {
  error: string;
  onLoadMore: () => void;
};

function ErrorList({ error, onLoadMore }: ErrorListProps) {
  const { t } = useTranslation();

  return (
    <div className="my-24 text-center text-gray-500 flex flex-col justify-center items-center h-32">
      <div className="space-y-2 max-w-[28rem]">
        <h3 className="font-semibold text-xl text-destructive">
          {t("tours.error.title")} ({error})
        </h3>
        <p className="text-md font-medium opacity-60">
          {t("tours.error.description")}
        </p>

        <Button
          variant="outline"
          className="h-8 rounded-full mt-4"
          onClick={() => onLoadMore()}
        >
          {t("tours.error.button")}
        </Button>
      </div>
    </div>
  );
}
